import React, { useState } from 'react';
import { isAfter } from 'date-fns';

import { Box, DateInput, DatePicker, FormField, FormHelp, Text } from '@zillow/constellation-10';

import { coerceToDate } from '@common/paymentsUtils/dateUtils';
import { formatDate, getLastSelectablePaymentDate } from '@common/paymentsUtils/dateUtils';
import Calendar from '@common/components/Calendar';
import PontoonConstants from '@common/constants/PontoonExceptionConstants';

interface Props {
  dueDate: string;
  selectedDate: string;
  changeSelectedDate: (selectedDate: string) => void;
}

const latePaymentText =
  'This is after the payment date your landlord chose. Make sure they are okay with getting the rent later.';

const ManualSchedulingPage = ({ dueDate, selectedDate, changeSelectedDate }: Props) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const isPaymentDateAfterDueDate = () => {
    const left = coerceToDate(selectedDate);
    const right = coerceToDate(dueDate);
    return !!left && !!right && isAfter(left, right);
  };

  const getLastSelectableDate = getLastSelectablePaymentDate(dueDate);
  const minDate = new Date();
  minDate.setDate(new Date().getDate() - 1);

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
      <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'default' }}>
        <Calendar
          title="Send date"
          dateLine1={formatDate(
            selectedDate,
            'MMM d',
            PontoonConstants.RENTER_HUB.PAYMENTS_SETTINGS.RENT_PAYMENT.DUE_DATE,
          )}
        />
        <FormField
          css={{ width: '100%' }}
          description={isPaymentDateAfterDueDate() ? <FormHelp error>{latePaymentText}</FormHelp> : null}
        >
          <DatePicker
            closeModalOnDateSelect
            dateFormat="MM/dd/yyyy"
            disabledDates={(date: Date) => date < minDate || date > getLastSelectableDate}
            onOpenChange={(open) => setIsCalendarOpen(open)}
            open={isCalendarOpen}
            input={
              <DateInput
                css={{ paddingLeft: 'sm' }}
                value={selectedDate}
                readOnly
                onClick={() => setIsCalendarOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setIsCalendarOpen(true);
                  }
                }}
              />
            }
            onValueChange={(value) => changeSelectedDate(String(value))}
            value={selectedDate}
          />
        </FormField>
      </Box>
      <Box>
        <Text textStyle="body-sm">
          We’ll email you a reminder 5 days before your payment is due each month. If this date falls on a weekend or
          holiday, we’ll send the rent <strong>1 business day later</strong>.
        </Text>
      </Box>
    </Box>
  );
};

export default ManualSchedulingPage;
